// Reports: date ranges, buckets and totals over the sums from repo.reports.
// Pure functions; the SQL is in packages/local (LocalRepository.reports).
import {
  adToBs,
  BS_MONTHS_EN,
  bsToAd,
  daysInBsMonth,
  fiscalYearOf,
  fiscalYearRange,
  formatIsoDate,
  NEPAL_FISCAL_YEAR,
  parseIsoDate,
  type FiscalYearSettings,
  type StockLevel,
} from '@ims/core'
import type {
  AssignedCustom,
  DaySale,
  LastSale,
  OpenInvoice,
  QuotationOutcome,
  ProductCostBasis,
  ProductProfitSales,
  ProductRow,
  ProductSales,
  SalesDay,
  TenantSettingsRow,
  VatDay,
} from '@ims/data'

export type Calendar = 'bs' | 'ad'
export type CalendarSettings = Pick<
  TenantSettingsRow,
  'fiscal_calendar' | 'fiscal_year_start_month' | 'fiscal_year_start_day' | 'date_display'
>

const DAY = 86_400_000
const AD_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function utc(iso: string): number {
  const d = parseIsoDate(iso)
  return Date.UTC(d.year, d.month - 1, d.day)
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

export function addDays(iso: string, days: number): string {
  return fromUtc(utc(iso) + days * DAY)
}

/** Whole days from `a` to `b` (negative when b is earlier). */
export function daysBetween(a: string, b: string): number {
  return Math.round((utc(b) - utc(a)) / DAY)
}

/** Months and day labels follow "Show dates in": English months only for 'ad'. */
export function calendarOf(
  settings: Pick<CalendarSettings, 'date_display'> | null | undefined,
): Calendar {
  return settings?.date_display === 'ad' ? 'ad' : 'bs'
}

function fiscalSettings(settings: CalendarSettings | null | undefined): FiscalYearSettings {
  if (!settings) return NEPAL_FISCAL_YEAR
  return {
    calendar: settings.fiscal_calendar,
    startMonth: settings.fiscal_year_start_month,
    startDay: settings.fiscal_year_start_day,
  }
}

interface Month {
  year: number
  month: number
}

function monthOf(iso: string, cal: Calendar): Month {
  const d = cal === 'bs' ? adToBs(iso) : parseIsoDate(iso)
  return { year: d.year, month: d.month }
}

function shiftMonth(m: Month, by: number): Month {
  const index = m.year * 12 + (m.month - 1) + by
  return { year: Math.floor(index / 12), month: (index % 12) + 1 }
}

/** First and last AD dates of a calendar month. */
function monthRange(m: Month, cal: Calendar): { from: string; to: string } {
  if (cal === 'ad') {
    const last = new Date(Date.UTC(m.year, m.month, 0)).getUTCDate()
    return {
      from: formatIsoDate({ ...m, day: 1 }),
      to: formatIsoDate({ ...m, day: last }),
    }
  }
  return {
    from: formatIsoDate(bsToAd({ ...m, day: 1 })),
    to: formatIsoDate(bsToAd({ ...m, day: daysInBsMonth(m.year, m.month) })),
  }
}

function monthLabel(m: Month, cal: Calendar): string {
  return cal === 'bs'
    ? `${BS_MONTHS_EN[m.month - 1]} ${m.year}`
    : `${AD_MONTHS[m.month - 1]} ${m.year}`
}

/** "11 Asoj" / "27 Sep": short enough for a chart axis. */
export function shortDay(iso: string, cal: Calendar): string {
  const d = cal === 'bs' ? adToBs(iso) : parseIsoDate(iso)
  return `${d.day} ${cal === 'bs' ? BS_MONTHS_EN[d.month - 1] : AD_MONTHS[d.month - 1]}`
}

/** First day (AD) of the month `today` falls in. */
export function monthStartOf(today: string, settings: CalendarSettings | null | undefined): string {
  return monthRange(monthOf(today, calendarOf(settings)), calendarOf(settings)).from
}

/**
 * The period in a file name: "2083-84" for a fiscal year, "Kartik-2083" for a month (the
 * months follow "Show dates in"), else the first and last day.
 */
export function periodSlug(
  key: PresetKey | 'custom',
  range: DateRange,
  settings: CalendarSettings | null | undefined,
): string {
  if (key === 'year' || key === 'last-year')
    return fiscalYearOf(range.from, fiscalSettings(settings)).replace('/', '-')
  if (key === 'month' || key === 'last-month')
    return monthLabel(monthOf(range.from, calendarOf(settings)), calendarOf(settings)).replace(
      ' ',
      '-',
    )
  return `${range.from}-to-${range.to}`
}

export type PresetKey = 'today' | 'week' | 'month' | 'last-month' | 'year' | 'last-year'

export interface DateRange {
  from: string
  to: string
}

export interface Preset extends DateRange {
  key: PresetKey
  label: string
}

/** Ranges offered in the picker. Weeks start on Sunday; months and years follow the calendar. */
export function rangePresets(
  today: string,
  settings: CalendarSettings | null | undefined,
): Preset[] {
  const cal = calendarOf(settings)
  const fiscal = fiscalSettings(settings)
  const weekday = new Date(utc(today)).getUTCDay()
  const month = monthOf(today, cal)
  const lastMonth = monthRange(shiftMonth(month, -1), cal)
  const year = fiscalYearOf(today, fiscal)
  const thisYear = fiscalYearRange(year, fiscal)
  const startYear = Number(year.slice(0, 4))
  const previous = `${startYear - 1}/${String(startYear % 100).padStart(2, '0')}`
  const lastYear = fiscalYearRange(previous, fiscal)
  return [
    { key: 'today', label: 'Today', from: today, to: today },
    { key: 'week', label: 'This week', from: addDays(today, -weekday), to: today },
    { key: 'month', label: 'This month', from: monthRange(month, cal).from, to: today },
    { key: 'last-month', label: 'Last month', ...lastMonth },
    { key: 'year', label: `This fiscal year (${year})`, from: thisYear.start, to: today },
    {
      key: 'last-year',
      label: `Last fiscal year (${previous})`,
      from: lastYear.start,
      to: lastYear.end,
    },
  ]
}

export type BucketSize = 'day' | 'week' | 'month'

export function bucketFor(range: DateRange): BucketSize {
  const days = daysBetween(range.from, range.to) + 1
  if (days <= 31) return 'day'
  if (days <= 120) return 'week'
  return 'month'
}

export interface SalesBucket extends DateRange {
  key: string
  label: string
  invoices: number
  sales: number
  returns: number
  /** Sales minus returns. */
  net: number
  /** VAT on sales minus VAT on returns. */
  vat: number
}

function bucketKey(iso: string, size: BucketSize, cal: Calendar, range: DateRange) {
  if (size === 'day') return { key: iso, from: iso, to: iso, label: shortDay(iso, cal) }
  if (size === 'week') {
    const start = addDays(iso, -new Date(utc(iso)).getUTCDay())
    const from = start < range.from ? range.from : start
    const end = addDays(start, 6)
    return { key: start, from, to: end > range.to ? range.to : end, label: shortDay(from, cal) }
  }
  const m = monthOf(iso, cal)
  const r = monthRange(m, cal)
  return {
    key: `${m.year}-${m.month}`,
    from: r.from < range.from ? range.from : r.from,
    to: r.to > range.to ? range.to : r.to,
    label: monthLabel(m, cal),
  }
}

/** One bucket per day, week or month of the range, empty ones included. */
export function bucketSales(
  days: readonly SalesDay[],
  range: DateRange,
  size: BucketSize,
  cal: Calendar,
): SalesBucket[] {
  const byDate = new Map(days.map((d) => [d.date, d]))
  const buckets = new Map<string, SalesBucket>()
  for (let iso = range.from; iso <= range.to; iso = addDays(iso, 1)) {
    const k = bucketKey(iso, size, cal, range)
    let b = buckets.get(k.key)
    if (!b) {
      b = { ...k, invoices: 0, sales: 0, returns: 0, net: 0, vat: 0 }
      buckets.set(k.key, b)
    }
    const d = byDate.get(iso)
    if (!d) continue
    b.invoices += d.invoices
    b.sales += d.total
    b.returns += d.returns
    b.net += d.total - d.returns
    b.vat += d.tax - d.returnsTax
  }
  return [...buckets.values()]
}

export interface SalesSummary {
  net: number
  sales: number
  returns: number
  invoices: number
  /** Average bill before returns; 0 when there are no bills. */
  average: number
  vat: number
}

export function salesSummary(days: readonly SalesDay[]): SalesSummary {
  const s = { net: 0, sales: 0, returns: 0, invoices: 0, average: 0, vat: 0 }
  for (const d of days) {
    s.sales += d.total
    s.returns += d.returns
    s.invoices += d.invoices
    s.vat += d.tax - d.returnsTax
  }
  s.net = s.sales - s.returns
  s.average = s.invoices ? Math.round(s.sales / s.invoices) : 0
  return s
}

/** How a bill was served; `counter` is a bill made in quick bill with no order behind it. */
export type ServiceKey = 'dine_in' | 'takeaway' | 'delivery' | 'counter'
const SERVICE_ORDER: readonly ServiceKey[] = ['dine_in', 'takeaway', 'delivery', 'counter']

export interface ServiceSales {
  key: ServiceKey
  bills: number
  total: number
}

/** A day's bills by the way they were served, in a fixed order; a way with no bill is left out. */
export function salesByService(rows: readonly DaySale[]): ServiceSales[] {
  const by = new Map<ServiceKey, ServiceSales>()
  for (const row of rows) {
    const key = row.serviceType ?? 'counter'
    const sum = by.get(key) ?? { key, bills: 0, total: 0 }
    sum.bills += 1
    sum.total += row.total
    by.set(key, sum)
  }
  return SERVICE_ORDER.flatMap((key) => by.get(key) ?? [])
}

export interface HourSales {
  /** 0 to 23, on the device's clock. */
  hour: number
  label: string
  total: number
}

/** "10 AM", "12 PM", "1 PM". */
export function hourLabel(hour: number): string {
  return `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? 'AM' : 'PM'}`
}

/**
 * A day's bills by the hour they were made: from the first hour with a sale to the last (or
 * to `nowHour`, the hour it is now), quiet hours included, and never fewer than six bars so
 * one busy hour does not fill the chart. `hourOf` reads the hour from a timestamp (the
 * device's clock by default).
 */
export function salesByHour(
  rows: readonly DaySale[],
  nowHour?: number,
  hourOf: (iso: string) => number = (iso) => new Date(iso).getHours(),
): HourSales[] {
  const totals = new Map<number, number>()
  for (const row of rows) {
    if (!row.finalizedAt) continue
    const hour = hourOf(row.finalizedAt)
    totals.set(hour, (totals.get(hour) ?? 0) + row.total)
  }
  if (totals.size === 0) return []
  const last = Math.max(...totals.keys(), nowHour ?? 0)
  const first = Math.max(0, Math.min(...totals.keys(), last - 5))
  return Array.from({ length: last - first + 1 }, (_, i) => ({
    hour: first + i,
    label: hourLabel(first + i),
    total: totals.get(first + i) ?? 0,
  }))
}

export interface ProductLine {
  key: string
  productId: string | null
  name: string
  qtyMilli: number
  returnQtyMilli: number
  netQtyMilli: number
  amount: number
  returnAmount: number
  net: number
  /** Share of all net sales in basis points (10000 = 100%). */
  shareBp: number
}

export function productLines(
  rows: readonly ProductSales[],
  names: ReadonlyMap<string, string>,
  by: 'amount' | 'qty',
): { lines: ProductLine[]; totalNet: number } {
  const totalNet = rows.reduce((a, r) => a + r.amount - r.returnAmount, 0)
  const lines = rows.map((r) => {
    const net = r.amount - r.returnAmount
    return {
      key: r.productId ?? `text:${r.description}`,
      productId: r.productId,
      name: (r.productId && names.get(r.productId)) || r.description,
      qtyMilli: r.qtyMilli,
      returnQtyMilli: r.returnQtyMilli,
      netQtyMilli: r.qtyMilli - r.returnQtyMilli,
      amount: r.amount,
      returnAmount: r.returnAmount,
      net,
      shareBp: totalNet > 0 ? Math.round((net * 10_000) / totalNet) : 0,
    }
  })
  lines.sort((a, b) =>
    by === 'qty'
      ? b.netQtyMilli - a.netQtyMilli || b.net - a.net
      : b.net - a.net || b.netQtyMilli - a.netQtyMilli,
  )
  return { lines, totalNet }
}

export interface UnsoldProduct {
  product: ProductRow
  stockMilli: number
}

/** Active stocked products with stock on hand that didn't sell in the period. */
export function unsoldProducts(
  products: readonly ProductRow[],
  levels: readonly StockLevel[],
  sales: readonly ProductSales[],
): UnsoldProduct[] {
  const sold = new Set(sales.filter((s) => s.qtyMilli > 0).map((s) => s.productId))
  const stock = new Map<string, number>()
  for (const l of levels) stock.set(l.product_id, (stock.get(l.product_id) ?? 0) + l.qty_milli)
  return products
    .filter((p) => p.is_active && p.track_stock && p.kind !== 'group' && !p.deleted_at)
    .map((product) => ({ product, stockMilli: stock.get(product.id) ?? 0 }))
    .filter((u) => u.stockMilli > 0 && !sold.has(u.product.id))
    .sort(
      (a, b) =>
        b.stockMilli * b.product.sell_price_paisa - a.stockMilli * a.product.sell_price_paisa ||
        a.product.name.localeCompare(b.product.name),
    )
}

export const AGE_BUCKETS = ['0–30 days', '31–60 days', '61–90 days', 'Over 90 days'] as const

export interface CustomerDues {
  key: string
  customerId: string | null
  name: string
  total: number
  invoices: number
  oldest: string
  /** Amounts per AGE_BUCKETS entry. */
  buckets: [number, number, number, number]
}

function ageBucket(days: number): 0 | 1 | 2 | 3 {
  if (days <= 30) return 0
  if (days <= 60) return 1
  if (days <= 90) return 2
  return 3
}

/** Open balances per customer, aged from the due date (or the bill date), largest first. */
export function ageDues(
  open: readonly OpenInvoice[],
  today: string,
): { customers: CustomerDues[]; totals: [number, number, number, number]; total: number } {
  const groups = new Map<string, CustomerDues>()
  const totals: [number, number, number, number] = [0, 0, 0, 0]
  for (const inv of open) {
    const key = inv.customerId ?? 'walk-in'
    let g = groups.get(key)
    if (!g) {
      g = {
        key,
        customerId: inv.customerId,
        name: inv.customerId ? inv.customerName || 'Customer' : 'Walk-in customers',
        total: 0,
        invoices: 0,
        oldest: inv.invoiceDate,
        buckets: [0, 0, 0, 0],
      }
      groups.set(key, g)
    }
    const b = ageBucket(daysBetween(inv.dueDate ?? inv.invoiceDate, today))
    g.buckets[b] += inv.balance
    totals[b] += inv.balance
    g.total += inv.balance
    g.invoices += 1
    if (inv.invoiceDate < g.oldest) g.oldest = inv.invoiceDate
    // The latest bill has the customer's current name.
    if (inv.customerId && inv.customerName) g.name = inv.customerName
  }
  const customers = [...groups.values()].sort((a, b) => b.total - a.total)
  return { customers, totals, total: totals.reduce((a, t) => a + t, 0) }
}

/** Days an order is past the day it was expected (0 when it is not late or has no day). */
export function daysLate(expected: string | null, today: string): number {
  return expected ? Math.max(0, daysBetween(expected, today)) : 0
}

export interface QuoteSummary {
  /** Quotations that left draft in the period. */
  quoted: { count: number; paisa: number }
  /** A bill or an order was made from it. */
  won: { count: number; paisa: number }
  /** Declined, cancelled, or left to expire. */
  lost: { count: number; paisa: number }
  /** Still with the customer: sent and not expired, or accepted and not billed yet. */
  open: { count: number; paisa: number }
  declined: number
  expired: number
  cancelled: number
  /** Won out of those decided (won + lost), in basis points; null when none is decided. */
  winRateBp: number | null
}

/** How quotations ended. Expired is worked out from the date, like everywhere else. */
export function quoteSummary(rows: readonly QuotationOutcome[], today: string): QuoteSummary {
  const sum = () => ({ count: 0, paisa: 0 })
  const out: QuoteSummary = {
    quoted: sum(),
    won: sum(),
    lost: sum(),
    open: sum(),
    declined: 0,
    expired: 0,
    cancelled: 0,
    winRateBp: null,
  }
  const add = (to: { count: number; paisa: number }, paisa: number) => {
    to.count += 1
    to.paisa += paisa
  }
  for (const q of rows) {
    add(out.quoted, q.totalPaisa)
    if (q.won) add(out.won, q.totalPaisa)
    else if (q.status === 'declined' || q.status === 'cancelled') {
      add(out.lost, q.totalPaisa)
      out[q.status] += 1
    } else if (q.status === 'sent' && q.validUntil !== null && q.validUntil < today) {
      add(out.lost, q.totalPaisa)
      out.expired += 1
    } else add(out.open, q.totalPaisa)
  }
  const decided = out.won.count + out.lost.count
  out.winRateBp = decided ? Math.round((out.won.count * 10_000) / decided) : null
  return out
}

export interface VatRate {
  rateBp: number
  taxable: number
  tax: number
  returnsTaxable: number
  returnsTax: number
}

export interface VatMonth extends DateRange {
  key: string
  label: string
  rates: VatRate[]
  netTaxable: number
  netTax: number
}

/** VAT per calendar month of the range, split by rate. */
export function vatByMonth(rows: readonly VatDay[], range: DateRange, cal: Calendar): VatMonth[] {
  const months = new Map<string, VatMonth & { byRate: Map<number, VatRate> }>()
  for (const r of rows) {
    const k = bucketKey(r.date, 'month', cal, range)
    let m = months.get(k.key)
    if (!m) {
      m = { ...k, rates: [], netTaxable: 0, netTax: 0, byRate: new Map() }
      months.set(k.key, m)
    }
    let rate = m.byRate.get(r.rateBp)
    if (!rate) {
      rate = { rateBp: r.rateBp, taxable: 0, tax: 0, returnsTaxable: 0, returnsTax: 0 }
      m.byRate.set(r.rateBp, rate)
    }
    rate.taxable += r.taxable
    rate.tax += r.tax
    rate.returnsTaxable += r.returnsTaxable
    rate.returnsTax += r.returnsTax
    m.netTaxable += r.taxable - r.returnsTaxable
    m.netTax += r.tax - r.returnsTax
  }
  return [...months.values()]
    .sort((a, b) => a.from.localeCompare(b.from))
    .map(({ byRate, ...m }) => ({
      ...m,
      rates: [...byRate.values()].sort((a, b) => b.rateBp - a.rateBp),
    }))
}

/** "13%" / "No VAT". */
export function rateLabel(rateBp: number): string {
  if (rateBp === 0) return 'No VAT'
  return `${rateBp % 100 === 0 ? rateBp / 100 : (rateBp / 100).toFixed(2)}%`
}

/** Rupees as a spreadsheet number (paisa / 100). */
export function rupees(paisa: number): number {
  return paisa / 100
}

/**
 * What one unit costs: the average cost of stock received (receipts and opening
 * stock with a cost), else the product's cost price, else unknown.
 */
export function unitCosts(
  products: readonly ProductRow[],
  basis: readonly ProductCostBasis[],
): Map<string, number> {
  const costs = new Map<string, number>()
  for (const p of products) {
    if (p.cost_price_paisa && p.cost_price_paisa > 0) costs.set(p.id, p.cost_price_paisa)
  }
  for (const b of basis) {
    if (b.qtyMilli > 0) costs.set(b.productId, (b.cost * 1000) / b.qtyMilli)
  }
  return costs
}

export interface ProfitLine {
  key: string
  productId: string | null
  name: string
  netQtyMilli: number
  /** Net sales before VAT (taxable amount after returns). */
  sales: number
  cost: number
  profit: number
  /** Profit as a share of sales, in basis points; null when sales are zero. */
  marginBp: number | null
}

export interface ProfitReport {
  lines: ProfitLine[]
  sales: number
  cost: number
  profit: number
  marginBp: number | null
  /** Products sold without a known cost: left out of the profit. */
  uncosted: { key: string; productId: string | null; name: string; sales: number }[]
  uncostedSales: number
}

export const CUSTOM_ASSIGNED_KEY = 'custom:assigned'

/**
 * Custom items with products assigned: their sales (before VAT, after returns)
 * and the cost of the assigned products. The share of a line that was returned
 * takes the same share of its cost back. A line with an assigned product of
 * unknown cost is left out, like an uncosted product.
 */
function assignedCustomProfit(
  assigned: AssignedCustom,
  costs: ReadonlyMap<string, number>,
): { costed: { sales: number; cost: number } | null; uncostedSales: number } {
  const byLine = new Map<string, AssignedCustom['products']>()
  for (const p of assigned.products) {
    const list = byLine.get(p.lineId)
    if (list) list.push(p)
    else byLine.set(p.lineId, [p])
  }
  let sales = 0
  let cost = 0
  let uncostedSales = 0
  let any = false
  for (const line of assigned.lines) {
    const net = line.taxable - line.returnTaxable
    let lineCost = 0
    let known = true
    for (const p of byLine.get(line.lineId) ?? []) {
      const unit = p.batchCost ?? costs.get(p.productId)
      if (unit === undefined) known = false
      else lineCost += (p.qtyMilli * unit) / 1000
    }
    if (!known) {
      uncostedSales += net
      continue
    }
    any = true
    sales += net
    cost += line.taxable > 0 ? (lineCost * net) / line.taxable : lineCost
  }
  return { costed: any ? { sales, cost: Math.round(cost) } : null, uncostedSales }
}

function margin(profit: number, sales: number): number | null {
  return sales === 0 ? null : Math.round((profit * 10_000) / sales)
}

/**
 * Gross profit per product: net sales before VAT minus the cost of what was
 * sold. Batches with a cost use it; the rest uses `unitCosts`. Returns take
 * back the same average cost. Products with no cost at all are listed apart.
 */
export function profitReport(
  sales: readonly ProductProfitSales[],
  costs: ReadonlyMap<string, number>,
  names: ReadonlyMap<string, string>,
  assigned?: AssignedCustom,
): ProfitReport {
  const lines: ProfitLine[] = []
  const uncosted: ProfitReport['uncosted'] = []
  const custom = assigned && assignedCustomProfit(assigned, costs)
  if (custom?.costed)
    lines.push({
      key: CUSTOM_ASSIGNED_KEY,
      productId: null,
      name: 'Custom items (products assigned)',
      netQtyMilli: 0,
      sales: custom.costed.sales,
      cost: custom.costed.cost,
      profit: custom.costed.sales - custom.costed.cost,
      marginBp: margin(custom.costed.sales - custom.costed.cost, custom.costed.sales),
    })
  if (custom?.uncostedSales)
    uncosted.push({
      key: `${CUSTOM_ASSIGNED_KEY}:uncosted`,
      productId: null,
      name: 'Custom items (an assigned product has no cost)',
      sales: custom.uncostedSales,
    })
  for (const r of sales) {
    const key = r.productId ?? `text:${r.description}`
    const name = (r.productId && names.get(r.productId)) || r.description
    const net = r.taxable - r.returnTaxable
    const unit = r.productId ? costs.get(r.productId) : undefined
    const unbatched = r.qtyMilli - r.batchQtyMilli
    if ((unbatched > 0 || (r.qtyMilli === 0 && r.returnQtyMilli > 0)) && unit === undefined) {
      uncosted.push({ key, productId: r.productId, name, sales: net })
      continue
    }
    const soldCost = r.batchCost + (unbatched * (unit ?? 0)) / 1000
    const perUnit = r.qtyMilli > 0 ? soldCost / r.qtyMilli : (unit ?? 0) / 1000
    const cost = Math.round(soldCost - r.returnQtyMilli * perUnit)
    lines.push({
      key,
      productId: r.productId,
      name,
      netQtyMilli: r.qtyMilli - r.returnQtyMilli,
      sales: net,
      cost,
      profit: net - cost,
      marginBp: margin(net - cost, net),
    })
  }
  lines.sort((a, b) => b.profit - a.profit || a.name.localeCompare(b.name))
  uncosted.sort((a, b) => b.sales - a.sales)
  const total = (pick: (l: ProfitLine) => number) => lines.reduce((a, l) => a + pick(l), 0)
  const salesTotal = total((l) => l.sales)
  const costTotal = total((l) => l.cost)
  return {
    lines,
    sales: salesTotal,
    cost: costTotal,
    profit: salesTotal - costTotal,
    marginBp: margin(salesTotal - costTotal, salesTotal),
    uncosted,
    uncostedSales: uncosted.reduce((a, u) => a + u.sales, 0),
  }
}

export const SALE_AGES = [
  'Sold in the last 30 days',
  '31–90 days ago',
  'Over 90 days ago',
  'Never sold',
] as const

export interface StockLine {
  product: ProductRow
  qtyMilli: number
  unitCost: number | null
  /** Stock at cost (null when the cost is unknown) and at the selling price. */
  costValue: number | null
  priceValue: number
  lastSold: string | null
  /** Index into SALE_AGES. */
  age: 0 | 1 | 2 | 3
}

export interface StockGroup {
  key: string
  name: string
  products: number
  costValue: number
  priceValue: number
  /** Products in the group with no known cost (left out of costValue). */
  missingCost: number
}

export interface StockValueReport {
  lines: StockLine[]
  categories: StockGroup[]
  costValue: number
  priceValue: number
  missingCost: number
  /** Value at cost and at price per SALE_AGES entry. */
  ages: { costValue: number; priceValue: number; products: number }[]
}

function saleAge(lastSold: string | null, today: string): 0 | 1 | 2 | 3 {
  if (!lastSold) return 3
  const days = daysBetween(lastSold, today)
  if (days <= 30) return 0
  if (days <= 90) return 1
  return 2
}

/** Stock on hand (optionally at one location) valued at cost and at price, by category and by last sale. */
export function stockValue(input: {
  products: readonly ProductRow[]
  levels: readonly StockLevel[]
  costs: ReadonlyMap<string, number>
  lastSales: readonly LastSale[]
  categoryNames: ReadonlyMap<string, string>
  locationId: string | null
  today: string
}): StockValueReport {
  const stock = new Map<string, number>()
  for (const l of input.levels) {
    if (input.locationId && l.location_id !== input.locationId) continue
    stock.set(l.product_id, (stock.get(l.product_id) ?? 0) + l.qty_milli)
  }
  const last = new Map(input.lastSales.map((s) => [s.productId, s.date]))
  const lines: StockLine[] = []
  for (const product of input.products) {
    if (
      !product.is_active ||
      !product.track_stock ||
      product.kind === 'group' ||
      product.deleted_at
    )
      continue
    const qtyMilli = stock.get(product.id) ?? 0
    if (qtyMilli <= 0) continue
    const unitCost = input.costs.get(product.id) ?? null
    const lastSold = last.get(product.id) ?? null
    lines.push({
      product,
      qtyMilli,
      unitCost,
      costValue: unitCost === null ? null : Math.round((unitCost * qtyMilli) / 1000),
      priceValue: Math.round((product.sell_price_paisa * qtyMilli) / 1000),
      lastSold,
      age: saleAge(lastSold, input.today),
    })
  }
  lines.sort((a, b) => (b.costValue ?? b.priceValue) - (a.costValue ?? a.priceValue))
  const groups = new Map<string, StockGroup>()
  const ages = SALE_AGES.map(() => ({ costValue: 0, priceValue: 0, products: 0 }))
  for (const l of lines) {
    const key = l.product.category_id ?? 'none'
    let g = groups.get(key)
    if (!g) {
      g = {
        key,
        name:
          (l.product.category_id && input.categoryNames.get(l.product.category_id)) ||
          'No category',
        products: 0,
        costValue: 0,
        priceValue: 0,
        missingCost: 0,
      }
      groups.set(key, g)
    }
    g.products += 1
    g.priceValue += l.priceValue
    if (l.costValue === null) g.missingCost += 1
    else g.costValue += l.costValue
    const a = ages[l.age]!
    a.products += 1
    a.priceValue += l.priceValue
    a.costValue += l.costValue ?? 0
  }
  const categories = [...groups.values()].sort(
    (a, b) => b.costValue - a.costValue || b.priceValue - a.priceValue,
  )
  return {
    lines,
    categories,
    costValue: categories.reduce((a, g) => a + g.costValue, 0),
    priceValue: categories.reduce((a, g) => a + g.priceValue, 0),
    missingCost: categories.reduce((a, g) => a + g.missingCost, 0),
    ages,
  }
}

/** Totals of Reports → Servers or Tables (`ServiceGroupSales` rows). Amounts in paisa. */
export interface GroupTotals {
  bills: number
  guests: number
  net: number
  voids: number
  voidAmount: number
  /** Net per guest and per bill, rounded; 0 when there are none. */
  perGuest: number
  perBill: number
}

export function groupTotals(
  rows: readonly {
    bills: number
    guests: number
    sales: number
    returns: number
    voids: number
    voidAmount: number
  }[],
): GroupTotals {
  const sum = (f: (r: (typeof rows)[number]) => number) => rows.reduce((n, r) => n + f(r), 0)
  const bills = sum((r) => r.bills)
  const guests = sum((r) => r.guests)
  const net = sum((r) => r.sales - r.returns)
  return {
    bills,
    guests,
    net,
    voids: sum((r) => r.voids),
    voidAmount: sum((r) => r.voidAmount),
    perGuest: guests > 0 ? Math.round(net / guests) : 0,
    perBill: bills > 0 ? Math.round(net / bills) : 0,
  }
}
